import type { UserInputTypes } from '@server/user-inputs';
import type { DocumentationUserInputMetadataElement } from '../../../types/documentationUserInputs';
import {
  ConfigurationTypeToDocumentationTabType,
  DocumentationTabTypes,
} from '../../../types/enums/DocumentationTabTypes';
import type { ConfigurationItemMap } from '../configuration-item-tables/shared/helpers';
import groupBy from 'lodash/groupBy';
import type { SalesforceItem } from '../../parser/ParserTypes';
import { createConfigurationItemFromParserResponseItem } from '../../parser/parserUtils';
import { ROLLUP, SWEEP_AUTOMATION } from '../../parser/ParserTypes';
import { AutomationType } from '../../../types/enums/AutomationType';
import { automationTypeToConfigurationType } from '../helpers';

export function addSalesforceItemToConfigurationItemMap(
  map: ConfigurationItemMap,
  item: SalesforceItem,
): void {
  const id = item.metadataId;
  if (!id) return;
  const key = item.parentType;
  const list = map[key] ?? (map[key] = []);
  if (!list.some((i) => i.metadataId === id)) {
    list.push(item);
  }
}

export function removeMetadataElementIdFromConfigurationItemMap(
  map: ConfigurationItemMap,
  metadataElementId: string,
): void {
  for (const k of Object.keys(map)) {
    const list = map[k];
    if (!list?.length) continue;
    map[k] = list.filter((i) => i.metadataId !== metadataElementId);
  }
}

export type TagConfigurationsState = Record<
  string,
  { [tagKey in UserInputTypes]: ConfigurationItemMap }
>;

/** Builds grid-selector summary from per-tag/user configuration maps (Redux `tagsConfigurations`). */
export function buildTagsAndUsersCountSummaryFromTagConfigurations(
  tagsConfigurations: TagConfigurationsState,
): Record<string, Record<string, string[]>> {
  const summary: Record<string, Record<string, string[]>> = {};

  const push = (rowId: string, metadataId: string, tab: DocumentationTabTypes) => {
    if (tab === DocumentationTabTypes.OTHER) return;
    if (!summary[rowId]) summary[rowId] = {};
    const bucket = summary[rowId][tab] ?? (summary[rowId][tab] = []);
    if (!bucket.includes(metadataId)) bucket.push(metadataId);
  };

  for (const [rowId, configs] of Object.entries(tagsConfigurations)) {
    for (const map of Object.values(configs)) {
      for (const items of Object.values(map)) {
        for (const item of items) {
          const metadataId = item.metadataId;
          if (!metadataId) continue;
          push(rowId, metadataId, documentationTabForParentType(item.parentType));
        }
      }
    }
  }

  return summary;
}

const parentTypeToTab = ConfigurationTypeToDocumentationTabType as Record<
  string,
  DocumentationTabTypes | undefined
>;

export function documentationTabForParentType(parentType: string): DocumentationTabTypes {
  return parentTypeToTab[parentType] ?? DocumentationTabTypes.OTHER;
}

export function configurationItemMapFromSalesforceItems(
  items: SalesforceItem[],
): ConfigurationItemMap {
  return groupBy(items, 'parentType') as ConfigurationItemMap;
}

function metadataElementToSalesforceItemLite(
  el: DocumentationUserInputMetadataElement,
): SalesforceItem {
  if (el.source === 'salesforce') {
    const cp = el.element;
    return createConfigurationItemFromParserResponseItem({
      responseItem: cp,
      keyType: cp.type,
      objectApiName: cp.objectName ?? '',
    });
  }
  if (el.source === 'automation') {
    const a = el.sweepAutomation;
    return {
      id: a?.automationId ?? '',
      name: a?.name ?? '',
      label: a?.name ?? '',
      type: a?.type ?? AutomationType.Default,
      objectApiNames: a?.objectName ? [a.objectName] : [],
      isActive: a?.isActive ?? false,
      metadataId: a?.automationId ?? '',
      parentType:
        automationTypeToConfigurationType(a?.type ?? AutomationType.Default) ?? SWEEP_AUTOMATION,
    } as SalesforceItem;
  }
  const r = el.sweepRollup;
  return {
    id: r.rollupId,
    name: r.rollupField?.sweepFieldName ?? '',
    label: r.rollupField?.sweepFieldName ?? '',
    objectApiNames: [r.objectName],
    type: r.rollupField?.fieldType,
    isActive: r.isActive,
    parentType: ROLLUP,
    metadataId: r.rollupId,
  } as SalesforceItem;
}

export function configurationItemMapFromApiMetadataElements(
  elements: DocumentationUserInputMetadataElement[] | undefined,
): ConfigurationItemMap {
  if (!elements?.length) return {};
  return configurationItemMapFromSalesforceItems(
    elements.map(metadataElementToSalesforceItemLite),
  );
}
